// src/routes/AppRoutes.js
import React, { useRef } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import Layout from "../layouts/Layout";

import SimpleTestPage from "../pages/SimpleTestPage";
import ContactUsPage from "../pages/ContactUsPage";
import AboutUsPage from "../pages/AboutUsPage";
import AuthCallbackPage from "../pages/AuthCallbackPage";
import WorkflowHistoryPageRemastered from "../pages/WorkflowHistoryPageRemastered";
import PendingTaskPageRemastred from "../pages/PendingTaskPageRemastred";
import UserCompaniesPage from "../pages/UserCompaniesPage";
import ManageUsersPage from "../pages/ManageUsersPage";
import LoginPage from "../pages/LoginPage";
import WorkflowHistoryView from "components/workflow/WorkflowHistoryView";
import { useAuth } from "../hooks/useAuth";
import ProtectedRoute from "components/ProtectedRoute";
import WorkflowListPage from "pages/WorkflowListPage";
import UnassignedTaskPage from "pages/UnassignedTaskPage";
import ManageGeneratorsPage from "pages/ManageGeneratorsPage";
import ManageContactsPage from "pages/ManageContactsPage";
import ManageTrucksPage from "pages/ManageTrucksPage";
import ManageTemplatesPage from "pages/ManageTemplatesPage";
import ManageBranchesPage from "pages/ManageBranchesPage";
import DynamicServicePage from "pages/DynamicServicePage";
import { logger } from "helpers/logger";
import UserManualsPage from "pages/UserManualsPage";
import FormViewRenderer from "components/forms/FormViewRenderer";

import ManageForms from "components/forms/ManageForms";
import EditForm from "components/forms/EditForm";
import FormViewer from "components/forms/FormViewer"
import ImportDataPage from "pages/ImportDataPage";
import ImportWizardPage from "pages/ImportWizardPage";
import DashboardPage from "pages/DashboardPage";
import DashboardGridPage from "pages/DashboardGridPage";

export default function AppRoutes({ loginKeycloakIframeFlag }) {
	const appRoutesId = useRef(Math.random().toString(36).substring(7));
	logger.info(`📍 AppRoutes ${appRoutesId.current} rendered`, {
		loginKeycloakIframe: loginKeycloakIframeFlag,
	});

	const keycloakAuth = useAuth();
	const isInIframe = window.self !== window.top;

	// Show loading while Keycloak initializes
	if (keycloakAuth.isLoading) {
		return (
			<div
				style={{
					display: "flex",
					justifyContent: "center",
					alignItems: "center",
					height: "100vh",
				}}
			>
				Loading...
			</div>
		);
	}

	// If inside silent SSO iframe, only render /auth/callback
	/* if (isInIframe) {
		console.log('🖼️ Running inside iframe - no Layout');
		return (
			<Routes>
				<Route path="/auth/callback" element={<AuthCallbackPage />} />
			</Routes>
		);
	} */

	// Main window SPA routes
	return (
		<Routes>
			{/* Routes WITH Layout */}
			<Route element={<Layout />}>
				<Route
					path="/"
					element={
						<ProtectedRoute>
							<Navigate to="/login" replace />
						</ProtectedRoute>
					}
				/>
				<Route
					path="/test"
					element={
						<ProtectedRoute>
							<SimpleTestPage />
						</ProtectedRoute>
					}
				/>

				<Route
					path="/workflowHistory"
					element={
						<ProtectedRoute>
							<WorkflowHistoryPageRemastered />
						</ProtectedRoute>
					}
				/>
				<Route
					path="/workflowList"
					element={
						<ProtectedRoute>
							<WorkflowListPage />
						</ProtectedRoute>
					}
				/>
				<Route
					path="/userTasks"
					element={
						<ProtectedRoute>
							<PendingTaskPageRemastred />
						</ProtectedRoute>
					}
				/>

				<Route
					path="/unassignedTasks"
					element={
						<ProtectedRoute>
							<UnassignedTaskPage />
						</ProtectedRoute>
					}
				/>
				<Route
					path="/userCompanies"
					element={
						<ProtectedRoute authOnly>
							<UserCompaniesPage />
						</ProtectedRoute>
					}
				/>
				<Route
					path="/manageUsers"
					element={
						<ProtectedRoute>
							<ManageUsersPage />
						</ProtectedRoute>
					}
				/>
				<Route
					path="/manageGenerators"
					element={
						<ProtectedRoute>
							<ManageGeneratorsPage />
						</ProtectedRoute>
					}
				/>
				<Route
					path="/manageContacts"
					element={
						<ProtectedRoute>
							<ManageContactsPage />
						</ProtectedRoute>
					}
				/>
				<Route
					path="/manage-contacts/:selectedCompanyId?"
					element={
						<ProtectedRoute>
							<ManageContactsPage />
						</ProtectedRoute>
					}
				/>
				<Route
					path="/manageFleet"
					element={
						<ProtectedRoute>
							<ManageTrucksPage />
						</ProtectedRoute>
					}
				/>
				<Route
					path="/manageTemplates"
					element={
						<ProtectedRoute>
							<ManageTemplatesPage />
						</ProtectedRoute>
					}
				/>
				<Route
					path="/manageBranch"
					element={
						<ProtectedRoute>
							<ManageBranchesPage />
						</ProtectedRoute>
					}
				/>
				<Route
					path="/import-data"
					element={
						<ProtectedRoute>
							<ImportDataPage />
						</ProtectedRoute>
					}
				/>
				<Route
					path="/import/:importTypeKey"
					element={
						<ProtectedRoute>
							<ImportWizardPage />
						</ProtectedRoute>
					}
				/>
				<Route
					path="/form_view_renderer"
					element={
						<ProtectedRoute>
							<FormViewRenderer />
						</ProtectedRoute>
					}
				/>
				<Route
					path="/manageForms"
					element={
						<ProtectedRoute>
							<ManageForms />
						</ProtectedRoute>
					}
				/>
				<Route
					path="/formedit/:id"
					element={
						<ProtectedRoute>
							<EditForm />
						</ProtectedRoute>
					}
				/>
				<Route
					path="/formView/:id"
					element={
						<ProtectedRoute>
							<FormViewer />
						</ProtectedRoute>
					}
				/>
				<Route
					path="/dashboards"
					element={
						<ProtectedRoute>
							<DashboardGridPage />
						</ProtectedRoute>
					}
				/>
				{/* Unproteted Routes */}
				<Route path="/services/:serviceName" element={<DynamicServicePage />} />
				<Route path="/manuals" element={<UserManualsPage />} />
				<Route path="/workflow-history-view" element={<WorkflowHistoryView />} />
				<Route path="/login" element={<LoginPage loginKeycloakIframeFlag={loginKeycloakIframeFlag} />} />
				<Route path="/contact" element={<ContactUsPage />} />
				<Route path="/aboutus" element={<AboutUsPage />} />
			</Route>

			{/* Routes WITHOUT Layout */}
			{/* <Route path="/login" element={<LoginPage loginKeycloakIframeFlag={loginKeycloakIframeFlag} />} /> */}
			<Route path="/auth/callback" element={<AuthCallbackPage />} />
		</Routes>
	);
}
